import React, { useEffect, useState, useMemo } from "react";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import {
   Dialog,
   DialogContent,
   DialogHeader,
   DialogTitle,
} from "@/Components/ui/dialog";
import { ScrollArea } from "@/Components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/Components/ui/tabs";
import { FolderTree, Home, ImageIcon, Search } from "lucide-react";
import { ImageType, FolderType } from "@/types/productTypes";
import { useAdminStore } from "@/store/useAdminStore";
import FolderItem from "@/Modules/Admin/ImageManager/folder/FolderItem";
import Breadcrumbs from "@/Modules/Admin/ImageManager/BreadCrumbs";
import ImageCard from "@/Modules/Admin/ImageManager/image/ImageCard";
import FolderCard from "@/Modules/Admin/ImageManager/folder/FolderCard";

interface ImagePickerDialogProps {
   open: boolean;
   onOpenChange: (open: boolean) => void;
   onImageSelect: (image: ImageType) => void;
   dialogTitle?: string;
}

const ImagePickerDialog: React.FC<ImagePickerDialogProps> = ({
   open,
   onOpenChange,
   onImageSelect,
   dialogTitle = "Select Image",
}) => {
   const [isLoading, setIsLoading] = useState(false);
   const [searchTerm, setSearchTerm] = useState("");
   const [view, setView] = useState<"grid" | "list">("grid");
   const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
   const [isSearching, setIsSearching] = useState(false);
   const [allReadyFetched, setAllReadyFetched] = useState(false);

   const { folders, allImages, fetchFolders, fetchImages } = useAdminStore();

   // Fetch images and folders when the dialog is opened
   useEffect(() => {
      if (open && !allReadyFetched) {
         fetchImagesAndFolders();
      }
   }, [open, allReadyFetched]);

   const fetchImagesAndFolders = async () => {
      try {
         setIsLoading(true);
         await fetchImages();
         await fetchFolders();
         setAllReadyFetched(true);
      } catch (error) {
         console.error("Error fetching data:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Get recent images from localStorage
   const recentImages = useMemo(() => {
      if (!allReadyFetched) return [];

      try {
         const recentIds: number[] = JSON.parse(
            localStorage.getItem("recentImages") || "[]"
         );
         return recentIds
            .map((id) => allImages.find((img) => img.id === id))
            .filter(Boolean)
            .slice(0, 6);
      } catch (error) {
         console.error("Error parsing recent images:", error);
         return [];
      }
   }, [allReadyFetched, allImages]);

   // Get child folders for the current folder
   const childFolders = useMemo(() => {
      return folders.filter((folder) => folder.parentId === currentFolderId);
   }, [folders, currentFolderId]);

   // Filter images based on search or current folder
   const currentFolderImages = useMemo(() => {
      if (searchTerm) {
         setIsSearching(true);
         // When searching, look through all images regardless of folder
         return allImages.filter(
            (image) =>
               image.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               image.description
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase())
         );
      } else {
         setIsSearching(false);
         // When not searching, show images from current folder
         return allImages.filter((image) =>
            currentFolderId === null
               ? !image.folderId
               : image.folderId === currentFolderId
         );
      }
   }, [allImages, currentFolderId, searchTerm]);

   const handleImageSelect = (image: ImageType) => {
      // Save recently selected images to localStorage
      try {
         const recentIds = JSON.parse(
            localStorage.getItem("recentImages") || "[]"
         );

         // Add image ID to the beginning of the array if not already present
         if (!recentIds.includes(image.id)) {
            recentIds.unshift(image.id);
            // Keep only the last 6 images
            const updatedRecentIds = recentIds.slice(0, 6);
            localStorage.setItem(
               "recentImages",
               JSON.stringify(updatedRecentIds)
            );
         }
      } catch (error) {
         console.error("Error updating recent images:", error);
      }

      onImageSelect(image);
   };

   return (
      <Dialog open={open} onOpenChange={onOpenChange}>
         <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0">
            <DialogHeader className="px-6 py-4 border-b">
               <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                     <DialogTitle>
                        {isLoading ? "Loading..." : dialogTitle}
                     </DialogTitle>
                  </div>
                  <div className="flex items-center space-x-2">
                     <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                           setView(view === "grid" ? "list" : "grid")
                        }
                     >
                        <ImageIcon className="h-4 w-4" />
                     </Button>
                  </div>
               </div>
               <div className="flex items-center space-x-4 mt-4">
                  <div className="relative flex-1">
                     <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                     <Input
                        placeholder="Search images..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                     />
                  </div>
               </div>
            </DialogHeader>

            {/* Add loading overlay */}
            {isLoading && (
               <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-50">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
               </div>
            )}

            <div className="flex flex-1 min-h-0">
               <div className="w-64 border-r flex flex-col">
                  <Tabs defaultValue="folders" className="flex-1">
                     <TabsList className="w-full">
                        <TabsTrigger value="folders" className="flex-1">
                           <FolderTree className="h-4 w-4 mr-2" />
                           Folders
                        </TabsTrigger>
                     </TabsList>
                     <ScrollArea className="flex-1">
                        <div className="p-4 space-y-1">
                           <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start"
                              onClick={() => setCurrentFolderId(null)}
                           >
                              <Home className="h-4 w-4 mr-2" />
                              Root
                           </Button>
                           {folders
                              .filter(
                                 (folder) => folder.parentId === currentFolderId
                              )
                              .map((folder) => (
                                 <FolderItem
                                    key={folder.id}
                                    folder={folder}
                                    onNavigate={setCurrentFolderId}
                                    isActive={folder.id === currentFolderId}
                                 />
                              ))}
                        </div>
                     </ScrollArea>
                  </Tabs>
               </div>

               <div className="flex-1 flex flex-col">
                  {/* Breadcrumbs navigation - hide when searching */}
                  {!isSearching && (
                     <div className="px-4 py-2 border-b">
                        <Breadcrumbs
                           folders={folders}
                           currentFolderId={currentFolderId}
                           onNavigate={setCurrentFolderId}
                        />
                     </div>
                  )}

                  {/* Show search indicator when searching */}
                  {isSearching && searchTerm && (
                     <div className="px-4 py-2 border-b bg-blue-50">
                        <p className="text-sm text-blue-700">
                           Searching all images for "{searchTerm}" (
                           {currentFolderImages.length} results)
                        </p>
                     </div>
                  )}

                  {/* Recent images section */}
                  {!isSearching &&
                     currentFolderId === null &&
                     recentImages.length > 0 && (
                        <div className="p-4 border-b">
                           <h3 className="text-sm font-medium mb-3">
                              Recent Images
                           </h3>
                           <div className="grid grid-cols-6 gap-2">
                              {recentImages.map((image) => (
                                 <div
                                    key={image?.id}
                                    className="relative cursor-pointer border rounded overflow-hidden hover:border-blue-500 transition-colors"
                                    onClick={() =>
                                       image && handleImageSelect(image)
                                    }
                                 >
                                    <img
                                       src={image?.url}
                                       alt={image?.title || "Recent image"}
                                       className="w-full h-16 object-cover"
                                    />
                                 </div>
                              ))}
                           </div>
                        </div>
                     )}

                  <ScrollArea className="flex-1 p-4">
                     {isSearching ? (
                        currentFolderImages.length > 0 ? (
                           <div
                              className={
                                 view === "grid"
                                    ? "grid grid-cols-3 gap-4"
                                    : "space-y-2"
                              }
                           >
                              {currentFolderImages.map((image) => (
                                 <ImageCard
                                    key={image.id}
                                    image={image}
                                    onSelect={handleImageSelect}
                                 />
                              ))}
                           </div>
                        ) : (
                           <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                              <Search className="h-12 w-12 mb-4 text-gray-300" />
                              <p>No images found matching "{searchTerm}"</p>
                              <Button
                                 variant="ghost"
                                 size="sm"
                                 onClick={() => setSearchTerm("")}
                                 className="mt-2"
                              >
                                 Clear search
                              </Button>
                           </div>
                        )
                     ) : (
                        <>
                           {/* Display folders first */}
                           {childFolders.length > 0 && (
                              <div className="mb-6">
                                 <h3 className="text-sm font-medium mb-3">
                                    Folders
                                 </h3>
                                 <div
                                    className={
                                       view === "grid"
                                          ? "grid grid-cols-3 gap-4"
                                          : "space-y-2"
                                    }
                                 >
                                    {childFolders.map((folder) => (
                                       <FolderCard
                                          key={folder.id}
                                          folder={folder}
                                          onNavigate={setCurrentFolderId}
                                       />
                                    ))}
                                 </div>
                              </div>
                           )}

                           {/* Display images */}
                           {currentFolderImages.length > 0 && (
                              <div>
                                 <h3 className="text-sm font-medium mb-3">
                                    Images
                                 </h3>
                                 <div
                                    className={
                                       view === "grid"
                                          ? "grid grid-cols-3 gap-4"
                                          : "space-y-2"
                                    }
                                 >
                                    {currentFolderImages.map((image) => (
                                       <ImageCard
                                          key={image.id}
                                          image={image}
                                          onSelect={handleImageSelect}
                                       />
                                    ))}
                                 </div>
                              </div>
                           )}

                           {/* Empty state when no folders or images */}
                           {childFolders.length === 0 &&
                              currentFolderImages.length === 0 && (
                                 <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                                    <ImageIcon className="h-12 w-12 mb-4 text-gray-300" />
                                    <p>
                                       No content in{" "}
                                       {currentFolderId === null
                                          ? "root folder"
                                          : "this folder"}
                                    </p>
                                 </div>
                              )}
                        </>
                     )}
                  </ScrollArea>
               </div>
            </div>
         </DialogContent>
      </Dialog>
   );
};

export default ImagePickerDialog;
